import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dumbbell, Utensils, Moon, Coffee } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import type { NewPlanItem } from '@/hooks/useWeeklyPlan';

interface AddPlanItemModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (item: Partial<NewPlanItem> & Pick<NewPlanItem, 'plan_date' | 'item_type' | 'title'>) => Promise<void>;
  date: string;
  editItem?: Partial<NewPlanItem> & { id?: string };
  onUpdate?: (id: string, updates: Partial<NewPlanItem>) => Promise<void>;
}

const ITEM_TYPES = [
  { value: 'workout', label: 'Workout', icon: Dumbbell, color: 'text-blue-400' },
  { value: 'meal', label: 'Meal', icon: Utensils, color: 'text-green-400' },
  { value: 'sleep', label: 'Sleep Schedule', icon: Moon, color: 'text-purple-400' },
  { value: 'rest_day', label: 'Rest Day', icon: Coffee, color: 'text-amber-400' },
] as const;

const MEAL_SLOTS = [
  { value: 'breakfast', label: 'Breakfast' },
  { value: 'lunch', label: 'Lunch' },
  { value: 'dinner', label: 'Dinner' },
  { value: 'snack', label: 'Snack' },
];

interface WorkoutOption {
  id: string;
  title: string;
  type: 'template' | 'program_day';
  programTitle?: string;
  dayNumber?: number;
}

interface SavedMealOption {
  id: string;
  name: string;
  meal_type: string;
  total_calories: number;
  total_protein: number;
}

export const AddPlanItemModal = ({ open, onClose, onAdd, date, editItem, onUpdate }: AddPlanItemModalProps) => {
  const { user } = useAuth();
  const isEditing = !!editItem?.id;
  const [itemType, setItemType] = useState<string>(editItem?.item_type || 'workout');
  const [title, setTitle] = useState(editItem?.title || '');
  const [description, setDescription] = useState(editItem?.description || '');
  const [mealSlot, setMealSlot] = useState(editItem?.meal_slot || 'breakfast');
  const [scheduledTime, setScheduledTime] = useState(editItem?.scheduled_time || '');
  const [sleepTime, setSleepTime] = useState(editItem?.sleep_time || '22:00');
  const [wakeTime, setWakeTime] = useState(editItem?.wake_time || '06:00');
  const [saving, setSaving] = useState(false);

  // Existing items for quick selection
  const [workoutOptions, setWorkoutOptions] = useState<WorkoutOption[]>([]);
  const [savedMealOptions, setSavedMealOptions] = useState<SavedMealOption[]>([]);
  const [selectedWorkout, setSelectedWorkout] = useState<string>('custom');
  const [selectedMeal, setSelectedMeal] = useState<string>('custom');

  // Fetch existing workouts & saved meals
  useEffect(() => {
    if (!user || !open) return;

    const fetchWorkouts = async () => {
      const options: WorkoutOption[] = [];

      // Fetch workout templates
      const { data: templates } = await supabase
        .from('workout_templates')
        .select('id, title')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (templates) {
        templates.forEach(t => options.push({ id: `template_${t.id}`, title: t.title, type: 'template' }));
      }

      // Fetch active program days
      const { data: programs } = await supabase
        .from('workout_programs')
        .select(`
          id, title, status,
          workout_program_days (id, day_number, day_name, workout_title)
        `)
        .eq('user_id', user.id)
        .in('status', ['active', 'draft'])
        .order('created_at', { ascending: false });

      if (programs) {
        programs.forEach(p => {
          const days = (p.workout_program_days || []) as any[];
          days
            .sort((a: any, b: any) => a.day_number - b.day_number)
            .forEach((d: any) => {
              options.push({
                id: `program_${d.id}`,
                title: d.workout_title,
                type: 'program_day',
                programTitle: p.title,
                dayNumber: d.day_number,
              });
            });
        });
      }

      setWorkoutOptions(options);
    };

    const fetchMeals = async () => {
      const { data: meals } = await supabase
        .from('saved_meals')
        .select('id, name, meal_type, total_calories, total_protein')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      setSavedMealOptions((meals || []) as SavedMealOption[]);
    };

    fetchWorkouts();
    fetchMeals();
  }, [user, open]);

  const handleWorkoutSelect = (value: string) => {
    setSelectedWorkout(value);
    if (value === 'custom') {
      setTitle('');
      return;
    }
    const option = workoutOptions.find(o => o.id === value);
    if (option) {
      setTitle(option.title);
      if (option.programTitle) {
        setDescription(`From program: ${option.programTitle}`);
      }
    }
  };

  const handleMealSelect = (value: string) => {
    setSelectedMeal(value);
    if (value === 'custom') {
      setTitle('');
      return;
    }
    const meal = savedMealOptions.find(m => m.id === value);
    if (meal) {
      setTitle(meal.name);
      setMealSlot(meal.meal_type || 'breakfast');
      setDescription(`${Math.round(meal.total_calories)} kcal · ${Math.round(meal.total_protein)}g protein`);
    }
  };

  const handleSave = async () => {
    if (!title.trim() && itemType !== 'rest_day' && itemType !== 'sleep') return;

    setSaving(true);

    // Resolve linked IDs
    let workout_template_id: string | null = null;
    let workout_program_day_id: string | null = null;
    let saved_meal_id: string | null = null;

    if (itemType === 'workout' && selectedWorkout !== 'custom') {
      const opt = workoutOptions.find(o => o.id === selectedWorkout);
      if (opt?.type === 'template') {
        workout_template_id = selectedWorkout.replace('template_', '');
      } else if (opt?.type === 'program_day') {
        workout_program_day_id = selectedWorkout.replace('program_', '');
      }
    }

    if (itemType === 'meal' && selectedMeal !== 'custom') {
      saved_meal_id = selectedMeal;
    }

    const payload: Partial<NewPlanItem> & Pick<NewPlanItem, 'plan_date' | 'item_type' | 'title'> = {
      plan_date: date,
      item_type: itemType as NewPlanItem['item_type'],
      title: itemType === 'rest_day' ? 'Rest Day' : itemType === 'sleep' ? 'Sleep Schedule' : title.trim(),
      description: description || null,
      meal_slot: itemType === 'meal' ? mealSlot : null,
      scheduled_time: scheduledTime || null,
      sleep_time: itemType === 'sleep' ? sleepTime : null,
      wake_time: itemType === 'sleep' ? wakeTime : null,
      is_completed: false,
      completed_at: null,
      workout_template_id,
      workout_program_day_id,
      saved_meal_id,
    };

    try {
      if (isEditing && onUpdate && editItem.id) {
        const { plan_date: _d, item_type: _t, title: _ti, ...updates } = payload;
        await onUpdate(editItem.id, { ...updates, title: payload.title });
      } else {
        await onAdd(payload);
      }
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const formattedDate = new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-card border-border max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-foreground">
            {isEditing ? 'Edit Plan Item' : 'Add to Plan'}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">{formattedDate}</p>
        </DialogHeader>

        <div className="space-y-4">
          {/* Type selector */}
          {!isEditing && (
            <div className="grid grid-cols-4 gap-2">
              {ITEM_TYPES.map(({ value, label, icon: Icon, color }) => (
                <button
                  key={value}
                  onClick={() => {
                    setItemType(value);
                    setSelectedWorkout('custom');
                    setSelectedMeal('custom');
                    if (value === 'rest_day') setTitle('Rest Day');
                    else if (value === 'sleep') setTitle('Sleep Schedule');
                    else setTitle('');
                    setDescription('');
                  }}
                  className={`flex flex-col items-center gap-1 p-3 rounded-xl border transition-all ${
                    itemType === value
                      ? 'border-primary bg-primary/10'
                      : 'border-border/50 bg-card/50 hover:bg-card'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${color}`} />
                  <span className="text-xs text-foreground">{label}</span>
                </button>
              ))}
            </div>
          )}

          {/* Workout picker */}
          {itemType === 'workout' && !isEditing && workoutOptions.length > 0 && (
            <div>
              <Label className="text-foreground">Choose Workout</Label>
              <Select value={selectedWorkout} onValueChange={handleWorkoutSelect}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select a workout..." />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="custom">✏️ Custom workout</SelectItem>
                  {workoutOptions.map(opt => (
                    <SelectItem key={opt.id} value={opt.id}>
                      {opt.type === 'program_day'
                        ? `${opt.programTitle} — Day ${opt.dayNumber}: ${opt.title}`
                        : opt.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Meal picker */}
          {itemType === 'meal' && !isEditing && savedMealOptions.length > 0 && (
            <div>
              <Label className="text-foreground">Choose Saved Meal</Label>
              <Select value={selectedMeal} onValueChange={handleMealSelect}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue placeholder="Select a meal..." />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  <SelectItem value="custom">✏️ Custom meal</SelectItem>
                  {savedMealOptions.map(meal => (
                    <SelectItem key={meal.id} value={meal.id}>
                      {meal.name} ({Math.round(meal.total_calories)} kcal)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Title */}
          {itemType !== 'rest_day' && itemType !== 'sleep' && (
            <div>
              <Label className="text-foreground">Title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={itemType === 'workout' ? 'e.g. Upper Body Push' : 'e.g. Chicken & Rice'}
                className="bg-background border-border"
              />
            </div>
          )}

          {/* Meal slot */}
          {itemType === 'meal' && (
            <div>
              <Label className="text-foreground">Meal Slot</Label>
              <Select value={mealSlot} onValueChange={setMealSlot}>
                <SelectTrigger className="bg-background border-border">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MEAL_SLOTS.map(s => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Scheduled time for workouts/meals */}
          {(itemType === 'workout' || itemType === 'meal') && (
            <div>
              <Label className="text-foreground">Time (optional)</Label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="bg-background border-border"
              />
            </div>
          )}

          {/* Sleep times */}
          {itemType === 'sleep' && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-foreground">Bedtime</Label>
                <Input
                  type="time"
                  value={sleepTime}
                  onChange={(e) => setSleepTime(e.target.value)}
                  className="bg-background border-border"
                />
              </div>
              <div>
                <Label className="text-foreground">Wake up</Label>
                <Input
                  type="time"
                  value={wakeTime}
                  onChange={(e) => setWakeTime(e.target.value)}
                  className="bg-background border-border"
                />
              </div>
            </div>
          )}

          {/* Description */}
          {itemType !== 'rest_day' && (
            <div>
              <Label className="text-foreground">Notes (optional)</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add notes..."
                className="bg-background border-border resize-none"
                rows={2}
              />
            </div>
          )}

          <Button
            onClick={handleSave}
            disabled={saving || (!title.trim() && itemType !== 'rest_day' && itemType !== 'sleep')}
            className="w-full bg-primary hover:bg-primary/90 text-accent-foreground font-semibold"
          >
            {saving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add to Plan'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
